// Reine Regeln für Rechnungskorrekturen (Spec 2026-09-09-rechnungskorrektur-design.md).
//
// Bewusst OHNE Datenbankzugriff: Vitest läuft in diesem Projekt in der node-Umgebung,
// und diese Regeln sind der Teil, der sich ohne Supabase prüfen lässt. Der Datenzugriff
// liegt in korrekturApi.ts — dieselbe Trennung wie zustaendigkeit.ts ↔ workflow.ts.

export interface KorrekturRechnung {
  id: string;
  unternehmen?: string | null;
  rechnungsnummer?: string | null;
  archiviert_am?: string | null;
  korrektur_angefordert_am?: string | null;
  korrigiert_durch_id?: string | null;
}

/** Vergleichsform für Lieferant und Nummer — dieselbe, die der Teil-Index benutzt. */
const firmaSchluessel = (wert?: string | null): string => (wert || '').trim().toLowerCase();

/**
 * Vergleichsform der Rechnungsnummer: nur trimmen, NICHT kleinschreiben.
 *
 * Bewusst enger als beim Firmennamen, damit Regel, Teil-Index und Datenbankabfrage
 * dasselbe sagen: der Index läuft über `(lower(trim(unternehmen)), rechnungsnummer)` —
 * die Nummer normalisiert er nicht — und die Kandidatenabfrage des Importeurs vergleicht
 * sie ebenfalls exakt. Würde hier zusätzlich kleingeschrieben, verspräche die Regel eine
 * Zuordnung, die der Server nie liefert.
 */
const nummerSchluessel = (wert?: string | null): string => (wert || '').trim();

/**
 * Darf für diese Rechnung eine Korrektur angefordert werden?
 *
 * Nicht bei archivierten (der Vorgang ist abgeschlossen), nicht bei bereits ersetzten
 * (dann ist die Nachfolgerin gemeint) und nicht doppelt: eine laufende Anforderung hält
 * die Rechnungsnummer bereits frei, eine zweite gäbe nur einen weiteren Mailversand.
 */
export function darfKorrekturAnfordern(r: KorrekturRechnung): boolean {
  return !r.archiviert_am && !r.korrigiert_durch_id && !r.korrektur_angefordert_am;
}

/** Zwischen Anforderung und Eintreffen der Nachfolgerin — der Zustand, der die Nummer freigibt. */
export function wartetAufKorrektur(r: KorrekturRechnung): boolean {
  return !!r.korrektur_angefordert_am && !r.korrigiert_durch_id;
}

/**
 * Empfängeradresse für die Korrekturanfrage: zuerst die Adresse, von der die Rechnung
 * kam, sonst die Dienstleister-Stammdaten, sonst nichts (der Nutzer wählt in der Pille).
 *
 * Eine unbrauchbare Adresse zählt wie gar keine: Graph weist die GESAMTE Mail ab, wenn
 * ein Empfänger ungültig ist (Lehre aus lib/serienmail/adresse.ts).
 */
export function korrekturEmpfaenger(
  quellMail?: string | null,
  dienstleisterMail?: string | null,
): string | null {
  for (const kandidat of [quellMail, dienstleisterMail]) {
    const wert = (kandidat || '').trim();
    if (wert.includes('@')) return wert;
  }
  return null;
}

/**
 * Ist der eingehende Beleg die erwartete Nachfolgerin einer angeforderten Korrektur?
 *
 * Nur bei identischem Lieferanten UND identischer Nummer UND laufender Anforderung.
 * Ohne diese Enge würde ein später eintreffender regulärer Beleg mit derselben Nummer
 * fälschlich als Korrektur gelten (Risiko in der Spec).
 */
export function istErwarteteNachfolgerin(
  kandidat: { unternehmen?: string | null; rechnungsnummer?: string | null },
  bestand: KorrekturRechnung[],
): KorrekturRechnung | null {
  const firma = firmaSchluessel(kandidat.unternehmen);
  const nummer = nummerSchluessel(kandidat.rechnungsnummer);
  if (!firma || !nummer) return null;
  return (bestand || []).find(
    (r) => wartetAufKorrektur(r)
      && firmaSchluessel(r.unternehmen) === firma
      && nummerSchluessel(r.rechnungsnummer) === nummer,
  ) || null;
}

/** Dateiname des Anhangs für die Korrekturanfrage (Muster signaturDateiname). */
export function korrekturDateiname(rechnungsnummer?: string | null): string {
  const nr = (rechnungsnummer || '').trim();
  if (!nr) return 'Rechnung.pdf';
  return `Rechnung_${nr}.pdf`.replace(/[^\wäöüÄÖÜß.\-]+/g, '_');
}

/** So viel eines Postgrest-Fehlers, wie die Übersetzung braucht. */
export interface SchreibFehler {
  code?: string | null;
  message?: string | null;
  details?: string | null;
}

/**
 * Deutscher Satz für die beiden Teil-Indizes der Korrektur-Migration.
 *
 * Warum überhaupt: Seit der Migration ist der Lieferantenname Teil des Schlüssels und
 * archivierte Belege sind ausgenommen. Damit sind zwei ganz normale Handgriffe neuerdings
 * ein 23505 — den Lieferantennamen zweier Schreibweisen zusammenführen (im Bestand:
 * „DIWA - Institut für Wasseranalytik GmbH" und „DIWA Institut für Wasseranalytik GmbH")
 * und eine archivierte Rechnung wiederherstellen, deren Beleg zwischenzeitlich neu
 * eingegangen ist. Der rohe Postgres-Text sagt dem Nutzer dazu nichts.
 *
 * Liefert null, wenn der Fehler nichts damit zu tun hat — dann bleibt die bisherige
 * Meldung stehen.
 */
export function korrekturFehlerText(fehler: unknown): string | null {
  const f = (fehler || {}) as SchreibFehler;
  if (String(f.code ?? '') !== '23505') return null;
  const text = `${f.message ?? ''} ${f.details ?? ''}`.toLowerCase();
  // Vor der Migration tragen dieselben Regeln noch die alten Namen — beide Namenspaare
  // erkennen, genau wie rechnung-import.ts es tut.
  if (text.includes('rechnungen_nummer_je_lieferant') || text.includes('rechnungen_rechnungsnummer_key')) {
    return 'Diese Rechnungsnummer ist für diesen Lieferanten bereits vergeben. '
      + 'Zwei Belege dürfen die Nummer nur teilen, solange für einen davon eine Korrektur angefordert ist — '
      + 'sonst muss der andere archiviert werden.';
  }
  if (text.includes('rechnungen_datei_hash_aktiv') || text.includes('rechnungen_datei_hash_key')) {
    return 'Dieselbe PDF-Datei ist bereits als aktive Rechnung erfasst. '
      + 'Die vorhandene Rechnung muss archiviert bleiben oder werden, bevor diese Datei erneut aktiv sein darf.';
  }
  return null;
}
